import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import Auth from '@aws-amplify/auth';

import { GetAuthAndClientDocument, AuthAndClient, AuthenticationType } from 'api/public';
import {
  ActionContainer,
  FormikEffect,
  Layouts,
  SubmitButton,
  ValidatedTextField,
} from 'components';
import { IWithApolloClient, withApolloClient } from 'providers';
import { useNotify } from 'hooks';
import { getSubdomainUrl } from 'helpers';
import * as paths from 'paths';
import Cookies from 'cookies';

import { GenericSidebarHeader, Main } from '../components';
import { ClientRedirectSchema, IClientRedirectSchema } from '../validations';

interface IProps extends IWithApolloClient {
  updateUsername: (username: string) => void;
  authData: {
    username: string;
  };
}

const ClientSelect: FC<IProps> = ({ apolloClient, authData, updateUsername }) => {
  const [working, setWorking] = useState(false);
  const notify = useNotify();
  const { t } = useTranslation();

  async function onSubmit(values: IClientRedirectSchema) {
    const username = values.username.toLowerCase();

    setWorking(true);

    const { data }: { data: { getAuthAndClient: AuthAndClient } } = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: GetAuthAndClientDocument,
      variables: { email_address: username },
    });

    if (!data || !data.getAuthAndClient) {
      setWorking(false);
      return notify.error({ message: t('auth:generalError') });
    }

    const { getAuthAndClient } = data;

    if (!getAuthAndClient.authentication_type) {
      setWorking(false);
      return notify.error({ message: t('auth:userNotFound') });
    }

    if (!getAuthAndClient.client) {
      setWorking(false);
      return notify.error({ message: t('auth:clientNotFound') });
    }

    const { authentication_type, existing_user } = getAuthAndClient;
    const { subdomain, sso_configuration } = getAuthAndClient.client;

    if (sso_configuration && authentication_type === AuthenticationType.Sso) {
      Auth.federatedSignIn({ customProvider: sso_configuration.provider });
    } else {
      // Use AuthenticationType.Auth for auth only user testing until backend supports the feature
      Cookies.userAuthType = authentication_type;

      const subdomainUrl = getSubdomainUrl(subdomain);
      const destinationUrl = `${subdomainUrl}${existing_user ? paths.login : paths.signUp}`;

      window.location.assign(destinationUrl);
    }
  }

  function handleChange({ username }: IClientRedirectSchema) {
    updateUsername(username);
  }

  function renderSidebar() {
    const initialValues: IClientRedirectSchema = { username: authData.username };

    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={ClientRedirectSchema}>
        {() => (
          <Form>
            <FormikEffect onChange={handleChange} delay={100} />
            <GenericSidebarHeader i18nKey="auth:signInExternal" />
            <ValidatedTextField name="username" i18nKey="user:email" />
            <ActionContainer>
              <SubmitButton working={working} text={t('auth:getStartedButton')} />
            </ActionContainer>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <Layouts.Full2Column
      sidebar={renderSidebar()}
      main={<Main headerI18nKey="auth:bodyHeader1" />}
    />
  );
};

export default withApolloClient(ClientSelect);
